import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, fileUrl } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import { Button, Callout, Dialog, ErrorState, Field, Input, Ltr, Textarea, useToast } from './ui';
import { whatsappNumber } from './IssueOrder';

/** A signature pad: draw with a finger (phone) or the mouse. */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const empty = useRef(true);
  useEffect(() => {
    const c = ref.current!;
    // sharp on high-density screens
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext('2d')!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
  }, []);
  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    empty.current = true;
    onChange(null);
  };
  return (
    <div>
      <canvas
        ref={ref}
        className="signature-pad"
        aria-label="חתימה"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          const ctx = e.currentTarget.getContext('2d')!;
          const p = point(e);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = e.currentTarget.getContext('2d')!;
          const p = point(e);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          empty.current = false;
        }}
        onPointerUp={(e) => {
          drawing.current = false;
          if (!empty.current) onChange(e.currentTarget.toDataURL('image/png'));
        }}
        onPointerCancel={() => (drawing.current = false)}
      />
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 4 }}>
        <span className="muted small">חותמים באצבע בתוך המסגרת</span>
        <Button size="sm" variant="ghost" onClick={clear}>ניקוי</Button>
      </div>
    </div>
  );
}

/** A phone photo shrunk to a sensible size before upload (a 12MP photo is 4–6MB). */
async function shrink(file: File, max = 1600): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
    return await new Promise((res) => c.toBlob((b) => res(b ?? file), 'image/jpeg', 0.82));
  } catch {
    return file;
  }
}

/**
 * At the customer: who received the goods, their signature and a photo. Saving marks the order
 * "סופק" and keeps the proof with the document.
 */
export function DeliveryDialog({
  kind,
  id,
  customerName,
  contact,
  onClose,
  onDone,
}: {
  kind: 'order' | 'document';
  id: number;
  customerName: string;
  contact: string | null;
  onClose: () => void;
  onDone: (r: { id: number; deliveredAt: string }) => void;
}) {
  const [name, setName] = useState(contact ?? '');
  const [signature, setSignature] = useState<string | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const photoUrl = photo ? URL.createObjectURL(photo) : null;
  useEffect(() => () => (photoUrl ? URL.revokeObjectURL(photoUrl) : undefined), [photoUrl]);

  const save = async () => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('recipientName', name.trim());
      if (note.trim()) form.append('note', note.trim());
      if (signature) form.append('signature', signature);
      if (photo) form.append('photo', await shrink(photo), 'delivery.jpg');
      const r = await api<{ id: number; deliveredAt: string }>(`/api/pick/${kind}/${id}/proof`, { form });
      toast.success('המסירה נשמרה — סומן כסופק');
      onDone(r);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`מסירה — ${customerName}`}
      footer={
        <>
          <Button onClick={onClose}>ביטול</Button>
          <Button variant="primary" loading={busy} disabled={name.trim().length < 2 || (!signature && !photo)} onClick={save}>
            אישור מסירה
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="שם המקבל" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="מי קיבל את הסחורה" autoComplete="off" />
        </Field>
        <Field label="חתימת המקבל">
          <SignaturePad onChange={setSignature} />
        </Field>
        <Field label="תמונה (לא חובה)" hint="של הסחורה במקום, או של התעודה החתומה">
          <div className="row wrap" style={{ gap: 10 }}>
            <label className="btn">
              📷 {photo ? 'צילום אחר' : 'צילום'}
              <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
            </label>
            {photoUrl ? <img src={photoUrl} alt="" style={{ height: 64, borderRadius: 6 }} /> : null}
          </div>
        </Field>
        <Field label="הערה">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="הושאר אצל השומר, חסר פריט…" />
        </Field>
        {!signature && !photo ? <div className="muted small">צריך חתימה או תמונה.</div> : null}
      </div>
    </Dialog>
  );
}

type Proof = {
  document: { id: number; type: string; typeLabel: string; number: string | null; issueDate: string | null; total: number | null; address: string | null; deliveredAt: string | null; lines: Array<{ description: string; quantity: number | null }> };
  customer: { id: number; name: string | null; phone: string | null; companyNumber: string | null } | null;
  proofs: Array<{ id: number; recipientName: string; signatureFileId: number | null; photoFileId: number | null; note: string | null; deliveredAt: string; userName: string | null }>;
};

export const proofText = (p: Proof) => {
  const last = p.proofs[0];
  if (!last) return '';
  return [
    `שלום ${p.customer?.name ?? ''},`,
    `הסחורה לפי ${p.document.typeLabel}${p.document.number ? ` מס׳ ${p.document.number}` : ''} נמסרה היום ל${last.recipientName} בשעה ${new Date(last.deliveredAt).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}.`,
    'תודה, מרקיט ציוד משרדי',
  ].join('\n');
};

/** The proof of delivery as a page: print it, or save as PDF from the browser. */
export function DeliveryProofPage() {
  const documentId = Number(useParams().documentId);
  const { data, error, refetch } = useQuery({ queryKey: ['delivery-proof', documentId], queryFn: () => api<Proof>(`/api/pick/proof/${documentId}`) });
  if (error) return <div className="pick-page"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data) return <div className="pick-page"><span className="spinner" /></div>;
  const phone = whatsappNumber(data.customer?.phone ?? null);
  return (
    <div className="pick-page proof-page">
      <div className="pick-top no-print">
        <Link to="/pick" className="btn sm ghost">→ ליקוט</Link>
        <span className="spacer" />
        {phone && data.proofs.length ? (
          <a className="btn sm" href={`https://wa.me/${phone}?text=${encodeURIComponent(proofText(data))}`} target="_blank" rel="noreferrer">WhatsApp ללקוח</a>
        ) : null}
        <Button size="sm" onClick={() => window.print()}>הדפסה / PDF</Button>
      </div>
      <header className="pick-head">
        <div className="muted small">מרקיט ציוד משרדי</div>
        <div className="pick-customer">אישור מסירה</div>
        <div className="pick-title">
          {data.document.typeLabel} {data.document.number ?? ''} · {fmtDate(data.document.issueDate)}
          {data.document.total != null ? ` · ${fmtMoney(data.document.total)}` : ''}
        </div>
        <div className="pick-meta">
          <strong>{data.customer?.name}</strong>
          {data.customer?.companyNumber ? <> · ח.פ <Ltr>{data.customer.companyNumber}</Ltr></> : null}
          {data.document.address ? ` · ${data.document.address}` : ''}
        </div>
      </header>
      {data.proofs.length === 0 ? <Callout tone="warn">עוד אין אישור מסירה למסמך הזה.</Callout> : null}
      {data.proofs.map((p) => (
        <section key={p.id} className="panel panel-body stack" style={{ marginBottom: 12 }}>
          <div>
            נמסר ל<strong>{p.recipientName}</strong> · {fmtDateTime(p.deliveredAt)}
            {p.userName ? <span className="muted"> · מסר: {p.userName}</span> : null}
          </div>
          {p.note ? <div className="small">הערה: {p.note}</div> : null}
          <div className="row wrap" style={{ gap: 16, alignItems: 'flex-start' }}>
            {p.signatureFileId ? (
              <figure style={{ margin: 0 }}>
                <img src={fileUrl(p.signatureFileId)} alt="חתימה" style={{ width: 260, maxWidth: '100%', border: '1px solid var(--line)', borderRadius: 8, background: '#fff' }} />
                <figcaption className="muted small">חתימת המקבל</figcaption>
              </figure>
            ) : null}
            {p.photoFileId ? (
              <figure style={{ margin: 0 }}>
                <a href={fileUrl(p.photoFileId)} target="_blank" rel="noreferrer"><img src={fileUrl(p.photoFileId)} alt="תמונה מהמסירה" style={{ width: 260, maxWidth: '100%', borderRadius: 8 }} /></a>
                <figcaption className="muted small">תמונה מהמסירה</figcaption>
              </figure>
            ) : null}
          </div>
        </section>
      ))}
      <section className="panel">
        <table className="table">
          <thead>
            <tr>
              <th>מוצר</th>
              <th className="num">כמות</th>
            </tr>
          </thead>
          <tbody>
            {data.document.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.description}</td>
                <td className="num tabular">{l.quantity ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
